import * as React from 'react';
import { useThemeProps } from '@mui/material/styles';
import type { DefaultizedProps } from '@mui/x-internals/types';
import type { LocalizedComponent, PickersInputLocaleText } from '@mui/x-date-pickers/locales';
import type { TimeStepOptions, TimeView } from '@mui/x-date-pickers/models';
import type {
  BasePickerInputProps,
  PickerViewRendererLookup,
  BaseTimeValidationProps,
  BaseClockProps,
  ExportedBaseClockProps,
  TimeViewWithMeridiem,
  UseViewsOptions,
  PickerRangeValue,
  PickerValue,
} from '@mui/x-date-pickers/internals';
import {
  applyDefaultViewProps,
  resolveTimeViewsResponse,
  useApplyDefaultValuesToTimeValidationProps,
} from '@mui/x-date-pickers/internals';
import type { TimeClockSlots, TimeClockSlotProps } from '@mui/x-date-pickers/TimeClock';
import type { DigitalClockSlots, DigitalClockSlotProps } from '@mui/x-date-pickers/DigitalClock';
import type {
  MultiSectionDigitalClockSlots,
  MultiSectionDigitalClockSlotProps,
} from '@mui/x-date-pickers/MultiSectionDigitalClock';
import type { TimeViewRendererProps } from '@mui/x-date-pickers/timeViewRenderers';
import { usePickerAdapter } from '@mui/x-date-pickers/hooks';
import type {
  TimeRangePickerToolbarProps,
  ExportedTimeRangePickerToolbarProps,
} from './TimeRangePickerToolbar';
import { TimeRangePickerToolbar } from './TimeRangePickerToolbar';
import type { TimeRangeValidationError } from '../models';
import type {
  ExportedTimeRangePickerTabsProps,
  TimeRangePickerTabsProps,
} from './TimeRangePickerTabs';
import { TimeRangePickerTabs } from './TimeRangePickerTabs';

export interface BaseTimeRangePickerSlots
  extends TimeClockSlots, DigitalClockSlots, MultiSectionDigitalClockSlots {
  /**
   * Tabs enabling toggling between start and end time.
   * @default TimeRangePickerTabs
   */
  tabs?: React.ElementType<TimeRangePickerTabsProps>;
  /**
   * Custom component for the toolbar rendered above the views.
   * @default TimeRangePickerToolbar
   */
  Toolbar?: React.JSXElementConstructor<TimeRangePickerToolbarProps>;
}

export interface BaseTimeRangePickerSlotProps
  extends TimeClockSlotProps, DigitalClockSlotProps, MultiSectionDigitalClockSlotProps {
  /**
   * Props passed down to the tabs component.
   */
  tabs?: ExportedTimeRangePickerTabsProps;
  /**
   * Props passed down to the toolbar component.
   */
  toolbar?: ExportedTimeRangePickerToolbarProps;
}

export type TimeRangePickerRenderers = PickerViewRendererLookup<
  PickerValue,
  TimeViewWithMeridiem,
  TimeViewRendererProps<TimeViewWithMeridiem, BaseClockProps<TimeViewWithMeridiem>> & {
    view: TimeViewWithMeridiem;
  }
>;

export interface BaseTimeRangePickerProps
  extends
    Omit<
      BasePickerInputProps<PickerRangeValue, TimeViewWithMeridiem, TimeRangeValidationError>,
      'orientation' | 'views' | 'openTo'
    >,
    ExportedBaseClockProps,
    Partial<Pick<UseViewsOptions<PickerRangeValue, TimeView>, 'openTo' | 'views'>> {
  /**
   * Overridable component slots.
   * @default {}
   */
  slots?: BaseTimeRangePickerSlots;
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps?: BaseTimeRangePickerSlotProps;
  /**
   * Define custom view renderers for each section.
   * If `null`, the section will only have field editing.
   * If `undefined`, internally defined view will be the used.
   */
  viewRenderers?: TimeRangePickerRenderers;
  /**
   * Amount of time options below or at which the single column time renderer is used.
   * @default 24
   */
  thresholdToRenderTimeInASingleColumn?: number;
  /**
   * The time steps between two time unit options.
   * For example, if `timeSteps.minutes = 8`, then the available minute options will be `[0, 8, 16, 24, 32, 40, 48, 56]`.
   * When single column time renderer is used, only `timeSteps.minutes` will be used.
   * @default{ hours: 1, minutes: 5, seconds: 5 }
   */
  timeSteps?: TimeStepOptions;
}

type UseTimeRangePickerDefaultizedProps<Props extends BaseTimeRangePickerProps> =
  LocalizedComponent<
    Omit<
      DefaultizedProps<Props, 'views' | 'openTo' | 'ampm' | keyof BaseTimeValidationProps>,
      'views'
    >
  > & {
    shouldRenderTimeInASingleColumn: boolean;
    views: readonly TimeViewWithMeridiem[];
    viewsForFormatting: readonly TimeViewWithMeridiem[];
  };

export function useTimeRangePickerDefaultizedProps<Props extends BaseTimeRangePickerProps>(
  props: Props,
  name: string,
): UseTimeRangePickerDefaultizedProps<Props> {
  const adapter = usePickerAdapter();
  const themeProps = useThemeProps({
    props,
    name,
  });

  const validationProps = useApplyDefaultValuesToTimeValidationProps(themeProps);
  const ampm = themeProps.ampm ?? adapter.is12HourCycleInCurrentLocale();

  const { openTo, views: defaultViews } = applyDefaultViewProps<TimeView>({
    views: themeProps.views,
    openTo: themeProps.openTo,
    defaultViews: ['hours', 'minutes'],
    defaultOpenTo: 'hours',
  });

  const localeText = React.useMemo<PickersInputLocaleText | undefined>(() => {
    if (themeProps.localeText?.toolbarTitle == null) {
      return themeProps.localeText;
    }

    return {
      ...themeProps.localeText,
      timeRangePickerToolbarTitle: themeProps.localeText.toolbarTitle,
    };
  }, [themeProps.localeText]);

  const {
    shouldRenderTimeInASingleColumn,
    thresholdToRenderTimeInASingleColumn,
    views,
    timeSteps,
  } = resolveTimeViewsResponse<TimeView, TimeViewWithMeridiem>({
    thresholdToRenderTimeInASingleColumn: themeProps.thresholdToRenderTimeInASingleColumn,
    ampm,
    timeSteps: themeProps.timeSteps,
    views: defaultViews,
  });

  // Keep the original views for format calculation (before filtering)
  const viewsForFormatting: readonly TimeViewWithMeridiem[] = ampm
    ? [...defaultViews, 'meridiem']
    : defaultViews;

  return {
    ...themeProps,
    ...validationProps,
    localeText,
    timeSteps,
    openTo,
    shouldRenderTimeInASingleColumn,
    thresholdToRenderTimeInASingleColumn,
    views,
    viewsForFormatting,
    ampm,
    slots: {
      tabs: TimeRangePickerTabs,
      toolbar: TimeRangePickerToolbar,
      ...themeProps.slots,
    },
    slotProps: {
      ...themeProps.slotProps,
      toolbar: {
        ampm,
        ...themeProps.slotProps?.toolbar,
      },
    },
  };
}
